import { useEffect, useState } from 'react';
import { Clock, RotateCcw, Trash2, X } from 'lucide-react';
import { historyStore, type CheckpointMeta } from '../../history/historyStore';
import { useCanvasStore } from '../../store/canvas-store';
import { usePanelStore } from '../panel/panel-store';

export function CheckpointsModal() {
  const checkpointsOpen = usePanelStore((s) => s.checkpointsOpen);
  const setCheckpointsOpen = usePanelStore((s) => s.setCheckpointsOpen);
  const [checkpoints, setCheckpoints] = useState<CheckpointMeta[]>([]);

  const refresh = () => { void historyStore.listCheckpoints().then(setCheckpoints); };

  useEffect(() => {
    if (checkpointsOpen) refresh();
  }, [checkpointsOpen]);

  // Escape closes.
  useEffect(() => {
    if (!checkpointsOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setCheckpointsOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [checkpointsOpen, setCheckpointsOpen]);

  if (!checkpointsOpen) return null;

  async function restore(id: string) {
    const doc = await historyStore.getCheckpoint(id);
    if (doc) useCanvasStore.getState().dispatch({ t: 'doc.restore', doc });
    setCheckpointsOpen(false);
  }

  async function del(id: string) {
    await historyStore.deleteCheckpoint(id);
    refresh();
  }

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onMouseDown={() => setCheckpointsOpen(false)}
    >
      <div
        onMouseDown={(e) => e.stopPropagation()}
        style={{ width: 'min(480px,92vw)', maxHeight: '75vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', background: 'var(--color-surface)', color: 'var(--color-text)', border: '1px solid var(--color-border)', borderRadius: 12, fontSize: 12 }}
      >
        {/* Title bar */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--color-border)]">
          <div className="flex items-center gap-2 font-sans font-semibold text-[13px]">
            <Clock size={14} className="text-[var(--color-text-muted)]" />
            Checkpoints
          </div>
          <button
            type="button"
            onClick={() => setCheckpointsOpen(false)}
            className="p-1 rounded hover:bg-[var(--color-menu-hover)] text-[var(--color-text-muted)]"
            title="Close"
          >
            <X size={14} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto p-2">
          {checkpoints.length === 0 ? (
            <div className="flex items-center justify-center h-24 font-sans text-[var(--color-text-muted)]">
              No checkpoints yet.
            </div>
          ) : (
            <ul className="flex flex-col gap-0.5">
              {checkpoints.map((cp) => (
                <li
                  key={cp.id}
                  className="flex items-center gap-2 px-2 py-1.5 rounded-[6px] hover:bg-[var(--color-surface-2)]"
                >
                  {/* Label + timestamp */}
                  <div className="flex-1 min-w-0">
                    <div className="font-sans text-[12px] text-[var(--color-text)] truncate">{cp.label}</div>
                    <div className="font-mono text-[10px] text-[var(--color-text-muted)]">
                      {new Date(cp.ts).toLocaleString()}
                    </div>
                  </div>

                  {/* Kind tag */}
                  <span className="font-sans text-[10px] px-1.5 py-0.5 rounded-[4px] shrink-0 border border-[var(--color-border)] text-[var(--color-text-muted)]">
                    {cp.kind === 'manual' ? 'manual' : 'auto'}
                  </span>

                  {/* Actions */}
                  <button
                    type="button"
                    className="flex items-center gap-1 font-sans text-[11px] text-[var(--color-text-muted)] hover:text-[var(--color-accent)] shrink-0 p-1 rounded"
                    onClick={() => { void restore(cp.id); }}
                    title="Restore this checkpoint"
                  >
                    <RotateCcw size={12} />
                  </button>
                  <button
                    type="button"
                    className="flex items-center gap-1 font-sans text-[11px] text-[var(--color-text-muted)] hover:text-[var(--color-danger)] shrink-0 p-1 rounded"
                    onClick={() => { void del(cp.id); }}
                    title="Delete this checkpoint"
                  >
                    <Trash2 size={12} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
